// Canvas & sails tracking (docs/canvas-plan.md), shared by the boat page, the Work Orders › Canvas
// tab, the techs app and the Dashboard card. Pure (no Firebase), so tests/unit/canvas.test.mjs runs it.
//
// One record per piece at /canvas/pieces/{id}. A piece is always in one of four places:
//   on-boat → stored (pulled, at the marina) → out (at a loft) → returned (back, at the marina) → on-boat
// Every move is one action: it changes only the fields it needs and adds one Log entry.

export const KINDS = [
  { key: 'bimini', label: 'Bimini', es: 'Bimini', group: 'canvas' },
  { key: 'dodger', label: 'Dodger', es: 'Capota', group: 'canvas' },
  { key: 'enclosure', label: 'Enclosure', es: 'Cerramiento', group: 'canvas' },
  { key: 'cockpit-cover', label: 'Cockpit cover', es: 'Funda de bañera', group: 'canvas' },
  { key: 'mooring-cover', label: 'Mooring cover', es: 'Funda de amarre', group: 'canvas' },
  { key: 'console-cover', label: 'Console cover', es: 'Funda de consola', group: 'canvas' },
  { key: 'canvas-other', label: 'Other canvas', es: 'Otra lona', group: 'canvas' },
  { key: 'mainsail', label: 'Mainsail', es: 'Vela mayor', group: 'sails' },
  { key: 'headsail', label: 'Jib / genoa', es: 'Foque / génova', group: 'sails' },
  { key: 'spinnaker', label: 'Spinnaker', es: 'Spinnaker', group: 'sails' },
  { key: 'sail-other', label: 'Other sail', es: 'Otra vela', group: 'sails' },
  { key: 'cushions', label: 'Cushions', es: 'Cojines', group: 'cushions' },
];
const KIND = Object.fromEntries(KINDS.map(k => [k.key, k]));

/** The filter pills, in order. */
export const GROUPS = [
  { key: 'all', label: 'All', es: 'Todo' },
  { key: 'canvas', label: 'Canvas', es: 'Lonas' },
  { key: 'sails', label: 'Sails', es: 'Velas' },
  { key: 'cushions', label: 'Cushions', es: 'Cojines' },
];

export const STATUSES = [
  { key: 'stored', label: 'Stored', es: 'Guardado' },
  { key: 'out', label: 'Out at loft', es: 'En la velería' },
  { key: 'returned', label: 'Returned', es: 'Devuelto' },
  { key: 'on-boat', label: 'On boat', es: 'En el barco' },
];
const STATUS = Object.fromEntries(STATUSES.map(s => [s.key, s]));

/**
 * What can be done to a piece. `from` = the statuses it starts in, `to` = where it ends,
 * `needs` = the input fields it can't go without.
 */
export const ACTIONS = {
  pull: { label: 'Mark pulled', es: 'Marcar quitado', from: ['on-boat'], to: 'stored', needs: ['location'] },
  // Straight off the boat too: the loft often collects it the day it comes off.
  sendOut: { label: 'Send out', es: 'Enviar', from: ['on-boat', 'stored', 'returned'], to: 'out', needs: ['vendorId'] },
  return: { label: 'Mark returned', es: 'Marcar devuelto', from: ['out'], to: 'returned', needs: ['location'] },
  move: { label: 'Move', es: 'Mover', from: ['stored', 'returned'], to: null, needs: ['location'] },
  install: { label: 'Put back on boat', es: 'Poner en el barco', from: ['stored', 'returned'], to: 'on-boat', needs: [] },
};

/** Why a move was refused, in words for the person (`canvasCode` on the thrown error, for translating). */
export const ERRORS = {
  'need-location': { EN: 'Say where it is stored.', ES: 'Indica dónde se guarda.' },
  'need-loft': { EN: 'Choose the loft it is going to.', ES: 'Elige la velería.' },
  'need-kind': { EN: 'Choose what kind of piece it is.', ES: 'Elige qué tipo de pieza es.' },
  'bad-date': { EN: 'Due back must be a date.', ES: 'La fecha de regreso no es válida.' },
  'same-spot': { EN: 'It is already there.', ES: 'Ya está ahí.' },
  'not-now': { EN: 'That can\'t be done to the piece where it is now.', ES: 'No se puede hacer con la pieza donde está ahora.' },
  'add-then-send': { EN: 'Add the piece first, then send it out.', ES: 'Agrega la pieza primero y luego envíala.' },
  'need-repair-note': { EN: 'Say what the repair is.', ES: 'Indica qué reparación es.' },
  'bad-amount': { EN: 'Amounts must be dollars, like 185 or 185.50.', ES: 'Los montos deben ser dólares, como 185 o 185.50.' },
  'need-cost': { EN: 'Enter what the loft charged.', ES: 'Indica lo que cobró la velería.' },
  'need-wo': { EN: 'Say which work order it goes on.', ES: 'Indica la orden de trabajo.' },
  'need-answer': { EN: 'Choose Approved or Declined.', ES: 'Elige Aprobada o Rechazada.' },
  'answered': { EN: 'This repair has already been answered.', ES: 'Esta reparación ya tiene respuesta.' },
};
function fail(code, message) {
  const err = new Error(message || ERRORS[code]?.EN || code);
  err.canvasCode = code;
  return err;
}
/** The message for a refused move in the person's language (EN | ES). */
export const errorText = (err, lang = 'EN') => (err?.canvasCode && ERRORS[err.canvasCode]?.[lang]) || err?.message || '';

export const REPAIR_STATUSES = [
  { key: 'needs-approval', label: 'Needs approval' },
  { key: 'sent', label: 'Sent to customer' },
  { key: 'approved', label: 'Approved' },
  { key: 'declined', label: 'Declined' },
];
const WAITING = ['needs-approval', 'sent'];

const str = (v, max = 200) => String(v == null ? '' : v).trim().slice(0, max);

const say = (item, lang) => (lang === 'ES' && item?.es) || item?.label;
export const kindLabel = (kind, lang) => say(KIND[kind], lang) || (lang === 'ES' ? 'Lona' : 'Canvas');
export const kindGroup = kind => KIND[kind]?.group || 'canvas';
export const statusLabel = (status, lang) => say(STATUS[status] || STATUS['on-boat'], lang);
export const actionLabel = (action, lang) => say(ACTIONS[action], lang);
export const groupLabel = (group, lang) => say(GROUPS.find(g => g.key === group), lang);
/** "Bimini" or "Bimini · Aft, 3 panels". */
export const pieceName = (p, lang) => [kindLabel(p?.Kind, lang), str(p?.Label, 80)].filter(Boolean).join(' · ');

/** Canvas comes off in the fall and goes back on in spring: a season runs Aug 1 to Jul 31. */
export function seasonStart(today) {
  const [y, m] = String(today).split('-').map(Number);
  return `${m >= 8 ? y : y - 1}-08-01`;
}
const dayMs = ymd => { const [y, m, d] = ymd.split('-').map(Number); return new Date(y, m - 1, d).getTime(); };
/** When this season began, as a timestamp (the board's "Back on boat" query starts here). */
export const seasonStartMs = today => dayMs(seasonStart(today));
/** Put back on the boat since this season began (the board's "Back on boat" column). */
export const installedThisSeason = (p, today) => p?.Status === 'on-boat' && Number(p.Installed_At) >= seasonStartMs(today);

/** Actions open to this piece now, in ACTIONS order. */
export function actionsFor(piece) {
  const status = piece?.Status || 'on-boat';
  return Object.keys(ACTIONS).filter(a => ACTIONS[a].from.includes(status));
}

/** A new piece: on the boat unless it is being logged as already pulled. */
export function newPiece({ customerId, boatId, kind, label, status, location, wo }) {
  if (!customerId || !boatId) throw new Error('A piece needs a customer and a boat.');
  if (!KIND[kind]) throw fail('need-kind');
  const start = STATUS[status] ? status : 'on-boat';
  if (start === 'out') throw fail('add-then-send');
  if (start !== 'on-boat' && !str(location)) throw fail('need-location');
  return {
    Customer_ID: customerId, Boat_ID: boatId, Kind: kind, Label: str(label, 120),
    Status: start, Location: start === 'on-boat' ? '' : str(location, 120), WO: str(wo, 40),
  };
}

/**
 * The fields to write for an action, and the Log entry to add. Throws a message fit to show
 * the user when the action can't be done. `input`: { location, vendorId, vendorName, dueBack, wo, note }.
 * The loft's name is kept on the piece and its log, so showing them never needs the vendor list.
 */
export function applyAction(piece, action, input = {}, { by = '', at = Date.now(), today = '' } = {}) {
  const a = ACTIONS[action];
  if (!a) throw new Error('Unknown action.');
  const from = piece?.Status || 'on-boat';
  if (!a.from.includes(from)) throw fail('not-now', `Can't do "${a.label}" while the piece is ${statusLabel(from).toLowerCase()}.`);
  const location = str(input.location, 120);
  const vendorId = str(input.vendorId, 80);
  const vendorName = str(input.vendorName, 120);
  if (a.needs.includes('location') && !location) throw fail('need-location');
  if (a.needs.includes('vendorId') && !vendorId) throw fail('need-loft');
  const dueBack = str(input.dueBack, 10);
  if (dueBack && !/^\d{4}-\d{2}-\d{2}$/.test(dueBack)) throw fail('bad-date');

  const to = a.to || from;
  const want = { Status: to };
  if (action === 'sendOut') Object.assign(want, { Location: '', Vendor_ID: vendorId, Vendor_Name: vendorName, Sent_On: today, Due_Back: dueBack });
  else if (action === 'install') Object.assign(want, { Location: '', Vendor_ID: '', Vendor_Name: '', Due_Back: '' });
  else if (action === 'return') Object.assign(want, { Location: location, Due_Back: '' });
  else Object.assign(want, { Location: location });
  if (input.wo) want.WO = str(input.wo, 40);

  // Only what actually changes (collaboration rule: never resend a whole record).
  const patch = {};
  Object.entries(want).forEach(([k, v]) => { if ((piece?.[k] ?? '') !== v) patch[k] = v; });
  if (action === 'move' && !Object.keys(patch).length) throw fail('same-spot');
  patch.Moved_At = at; // the last move (the board's "since"; a piece that never moved can still be removed)
  if (action === 'install') patch.Installed_At = at;

  const log = { At: at, By: str(by, 80), Action: action, From: from, To: to };
  if (location && action !== 'sendOut' && action !== 'install') log.Location = location;
  if (action === 'sendOut') Object.assign(log, { Vendor_ID: vendorId, Vendor_Name: vendorName });
  if (action === 'return' && piece?.Vendor_ID) Object.assign(log, { Vendor_ID: piece.Vendor_ID, Vendor_Name: piece.Vendor_Name || '' });
  if (dueBack && action === 'sendOut') log.Due_Back = dueBack;
  const note = str(input.note, 500);
  if (note) log.Note = note;
  if (input.wo) log.WO = str(input.wo, 40);
  return { patch, log };
}

const CANVAS_WORDS = /\b(canvas|bimini|dodger|enclosure|sails?|mainsail|jib|genoa|cushions?|cockpit cover|mooring cover|console cover)\b/i;
/** An operation about canvas, sails or cushions (the techs app shows the boat's pieces on it). */
export function isCanvasOp(op) {
  if (!op) return false;
  if (op.Op_Type === 'Canvas') return true;
  return CANVAS_WORDS.test(`${op.Short_Description || ''} ${op.Description || ''} ${op.Short_Description_DM || ''}`);
}

// What finishing a canvas op means for the pieces (storage-contract services: "Remove canvas — store
// in boat", "Canvas — send out for cleaning", "Reinstall canvas"). Checked in this order.
const OP_MOVES = [
  { action: 'install', words: /\b(re-?install|install|put (it |them )?back)\b/i },
  { action: 'sendOut', words: /\b(send|sent|loft|clean(ing|ed)?|repairs?|re-?stitch)\b/i },
  { action: 'pull', words: /\b(remove|removal|pull|take off|strip|store)\b/i },
];
const opText = op => `${op?.Short_Description || ''} ${op?.Description || ''} ${op?.Short_Description_DM || ''}`;

/**
 * The move a finished canvas op implies, and which of the boat's pieces it would apply to:
 * { action, pieces: [...], away: [...] } or null. `away` = pieces that can't follow (still out at a loft
 * when the op puts canvas back on). `location`: the spot to suggest ("In the boat" for "store in boat").
 */
export function opMove(op, pieces) {
  if (!isCanvasOp(op)) return null;
  const text = opText(op);
  const move = OP_MOVES.find(m => m.words.test(text));
  if (!move) return null;
  const mine = pieces.filter(p => p && p.Boat_ID === op.Boat_ID);
  const can = mine.filter(p => ACTIONS[move.action].from.includes(p.Status || 'on-boat'));
  const away = move.action === 'install' ? mine.filter(p => p.Status === 'out') : [];
  if (!can.length && !away.length) return null;
  return { action: move.action, pieces: can, away, location: /\bstored? (it |them )?(in|aboard) (the )?boat\b/i.test(text) ? 'In the boat' : '' };
}

/** Pieces out at a loft, by boat: Map "customerId|boatId" → [pieces] (the Haul/Launch board's flag). */
export function outByBoat(pieces) {
  const map = new Map();
  pieces.forEach(p => {
    if (p?.Status !== 'out') return;
    const key = `${p.Customer_ID}|${p.Boat_ID}`;
    if (!map.has(key)) map.set(key, []);
    map.get(key).push(p);
  });
  return map;
}

/** "Bimini at North Sails (due back 2027-03-01)" per piece, one per line. */
export const outText = pieces => pieces.map(p => `${pieceName(p)} at ${p.Vendor_Name || 'the loft'}${p.Due_Back ? ` (due back ${p.Due_Back})` : ''}`).join('\n');

/** Out at a loft past its due-back date. */
export function isOverdue(piece, today) {
  return piece?.Status === 'out' && !!piece.Due_Back && piece.Due_Back < today;
}

// ---- Repairs the loft found, and the customer's answer (docs/canvas-plan.md session 3) ----
//   Repairs/{id}: Note, Quote_Cents (or none), Status needs-approval → sent (emailed) → approved | declined,
//   Added_At/By, Sent_At, Sign_Token, Decided_At, Decided_Via (email | phone | in-person), Decided_By
//   (the customer's typed name for email; the staff member who took the call otherwise).

/** "$185.00" */
export const money = cents => `$${((Number(cents) || 0) / 100).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',')}`;

/** Dollars typed by a person → cents (null when blank). Throws 'bad-amount' for anything else. */
export function toCents(value) {
  const s = str(value, 20).replace(/[$,\s]/g, '');
  if (!s) return null;
  if (!/^\d+(\.\d{1,2})?$/.test(s)) throw fail('bad-amount');
  return Math.round(parseFloat(s) * 100);
}

/** A new repair note: { Note, Quote_Cents?, Status, Added_At, Added_By }. */
export function newRepair({ note, quote, by = '', at = Date.now() }) {
  const n = str(note, 500);
  if (!n) throw fail('need-repair-note');
  const cents = toCents(quote);
  const r = { Note: n, Status: 'needs-approval', Added_At: at, Added_By: str(by, 80) };
  if (cents != null) r.Quote_Cents = cents;
  return r;
}

/** The customer's answer (or the office recording one): the fields to write on the repair. */
export function answerRepair(repair, decision, { via, by = '', at = Date.now() }) {
  if (!repair || !WAITING.includes(repair.Status)) throw fail('answered');
  if (decision !== 'approved' && decision !== 'declined') throw fail('need-answer');
  return { Status: decision, Decided_At: at, Decided_Via: via, Decided_By: str(by, 120) };
}

const VIA = { email: 'by email', phone: 'by phone', 'in-person': 'in person' };
/** "Approved by email · Jane Ortiz · Oct 24" style words for a decided repair (no date). */
export const decidedText = r => [r.Status === 'approved' ? 'Approved' : 'Declined', VIA[r.Decided_Via] || '', r.Decided_By ? `· ${r.Decided_By}` : ''].filter(Boolean).join(' ');

/**
 * What the customer is asked to approve for one boat: every repair still waiting on its pieces.
 * The approval page shows this and the signed record keeps it. Items are keyed "pieceId~repairId".
 * Returns null when nothing is waiting.
 */
export function repairSnapshot(pieces, { boatName = '', customerName = '' } = {}) {
  const items = [];
  pieces.forEach(p => Object.entries(p.Repairs || {}).forEach(([rid, r]) => {
    if (!r || !WAITING.includes(r.Status)) return;
    items.push({ key: `${p.id}~${rid}`, label: pieceName(p), note: str(r.Note, 500), cents: Number.isFinite(r.Quote_Cents) ? r.Quote_Cents : null, at: r.Added_At || 0 });
  }));
  if (!items.length) return null;
  items.sort((a, b) => a.label.localeCompare(b.label) || a.at - b.at);
  return {
    kind: 'repair',
    title: 'Repairs to approve',
    subtitle: [str(boatName, 80), 'canvas, sails and cushions'].filter(Boolean).join(' · '),
    customerName: str(customerName, 120),
    items: items.map(({ key, label, note, cents }) => ({ key, label, note, cents })),
    totalCents: items.reduce((s, i) => s + (i.cents || 0), 0),
    action: 'choose',
  };
}

/**
 * A loft's invoice as a line on the work order: a hand-priced part (billing reads the "$amount" in
 * the part number), charged at `charge` (defaults to the loft's cost).
 */
export function loftInvoiceLine(piece, { vendorName = '', invoiceNo = '', cost, charge }) {
  const costCents = toCents(cost);
  if (costCents == null) throw fail('need-cost');
  const chargeCents = toCents(charge) ?? costCents;
  const loft = str(vendorName, 120) || str(piece?.Vendor_Name, 120) || 'Loft';
  const inv = str(invoiceNo, 40);
  return {
    costCents, chargeCents, loft, invoiceNo: inv,
    part: {
      Part_Number: `SUBLET ${money(chargeCents)}`,
      Description: `${loft}${inv ? ` inv ${inv}` : ''}: ${pieceName(piece)}${chargeCents !== costCents ? ` (loft cost ${money(costCents)})` : ''}`,
      Quantity: 1,
    },
  };
}

/**
 * The Dashboard's Canvas & sails card: repairs not yet sent, repairs waiting on the customer,
 * pieces overdue at a loft and pieces out at all; plus the pieces that need someone (overdue first,
 * then repairs to send, then repairs waiting), each with why.
 */
export function canvasSummary(pieces, today) {
  const s = { toSend: 0, waiting: 0, overdue: 0, out: 0, rows: [] };
  pieces.forEach(p => {
    const reps = Object.values(p.Repairs || {}).filter(Boolean);
    const toSend = reps.filter(r => r.Status === 'needs-approval').length;
    const waiting = reps.filter(r => r.Status === 'sent').length;
    s.toSend += toSend;
    s.waiting += waiting;
    if (p.Status === 'out') s.out++;
    const late = isOverdue(p, today);
    if (late) s.overdue++;
    if (late) s.rows.push({ id: p.id, rank: 0, why: `overdue at ${p.Vendor_Name || 'the loft'} (due ${p.Due_Back})` });
    else if (toSend) s.rows.push({ id: p.id, rank: 1, why: `${toSend} repair${toSend === 1 ? '' : 's'} to send for approval` });
    else if (waiting) s.rows.push({ id: p.id, rank: 2, why: `${waiting} repair${waiting === 1 ? '' : 's'} waiting on the customer` });
  });
  s.rows.sort((a, b) => a.rank - b.rank);
  return s;
}

/** Repairs still waiting on the customer, oldest first. */
export function waitingRepairs(piece) {
  return Object.entries(piece?.Repairs || {})
    .filter(([, r]) => r && WAITING.includes(r.Status))
    .map(([id, r]) => ({ id, ...r }));
}

/** Pieces in a filter-pill group (`all` = every piece). */
export const inGroup = (piece, group) => !group || group === 'all' || kindGroup(piece?.Kind) === group;

/** How many pieces each pill would show: { all, canvas, sails, cushions }. */
export function groupCounts(pieces) {
  const counts = Object.fromEntries(GROUPS.map(g => [g.key, 0]));
  pieces.forEach(p => { counts.all++; counts[kindGroup(p.Kind)]++; });
  return counts;
}

/**
 * Pieces sorted into board columns. `pieces`: [{ id, ...piece }]. `names(piece)` gives the
 * customer/boat/vendor text that search matches. Options: group, vendorId, overdueOnly, search, today.
 * "On boat" only lists pieces put back on this season (the rest have simply stayed aboard).
 */
export function buildBoard(pieces, { group = 'all', vendorId = '', overdueOnly = false, search = '', today = '', names = () => '' } = {}) {
  const q = str(search).toLowerCase();
  const columns = Object.fromEntries(STATUSES.map(s => [s.key, []]));
  pieces.forEach(p => {
    if (!inGroup(p, group)) return;
    if (vendorId && p.Vendor_ID !== vendorId) return;
    if (overdueOnly && !isOverdue(p, today)) return;
    if (p.Status === 'on-boat' && today && !installedThisSeason(p, today)) return;
    if (q && !`${pieceName(p)} ${p.Location || ''} ${p.WO || ''} ${p.Vendor_Name || ''} ${names(p)}`.toLowerCase().includes(q)) return;
    (columns[p.Status] || columns['on-boat']).push(p);
  });
  // Overdue first, then the longest out / soonest due.
  const key = p => `${isOverdue(p, today) ? 0 : 1}${p.Due_Back || '9999'}${p.Sent_On || ''}${pieceName(p)}`;
  Object.values(columns).forEach(list => list.sort((a, b) => key(a).localeCompare(key(b))));
  return columns;
}

/** One line of history for a Log entry: "Sent to North Sails (due back 2027-03-01)". */
export function describeLog(entry) {
  const loft = entry?.Vendor_Name || 'the loft';
  let text;
  switch (entry?.Action) {
    case 'pull': text = `Pulled, stored at ${entry.Location}`; break;
    case 'sendOut': text = `${entry.From === 'on-boat' ? 'Taken off the boat and sent' : 'Sent'} to ${loft}${entry.Due_Back ? ` (due back ${entry.Due_Back})` : ''}`; break;
    case 'return': text = `Back from ${loft}, stored at ${entry.Location}`; break;
    case 'move': text = `Moved to ${entry.Location}`; break;
    case 'install': text = 'Put back on the boat'; break;
    case 'add': text = entry.To === 'on-boat' ? 'Added (on the boat)' : `Added, stored at ${entry.Location}`; break;
    case 'repairAdd': text = `Repair noted: ${entry.Repair}${entry.Quote_Cents != null ? ` (${money(entry.Quote_Cents)})` : ''}`; break;
    case 'repairSent': text = `Repair sent to the customer to approve: ${entry.Repair}`; break;
    case 'repairRemove': text = `Repair note removed: ${entry.Repair}`; break;
    case 'repairAnswer': text = `Repair ${entry.Decision === 'approved' ? 'approved' : 'declined'} ${VIA[entry.Via] || ''}${entry.Who ? ` (${entry.Who})` : ''}: ${entry.Repair}`; break;
    case 'cost': text = `Loft invoice${entry.Invoice_No ? ` ${entry.Invoice_No}` : ''} ${money(entry.Cost_Cents)}${entry.WO ? `, added to WO ${entry.WO}` : ''}`; break;
    default: text = 'Updated';
  }
  return entry?.Note ? `${text}. ${entry.Note}` : text;
}

/**
 * The same move for several pieces at once (the board's batch Send out / Mark returned).
 * Returns { moves: [{ piece, patch, log }], refused: [{ piece, err }] }: a refused piece doesn't
 * stop the others.
 */
export function planBatch(pieces, action, input, ctx) {
  const moves = [];
  const refused = [];
  pieces.forEach(piece => {
    try { moves.push({ piece, ...applyAction(piece, action, input, ctx) }); } catch (err) { refused.push({ piece, err }); }
  });
  return { moves, refused };
}

/**
 * A boat's canvas history for Service history, newest first (`logs`: { pieceId: { key: entry } }):
 * [{ at, pieceId, piece: 'Bimini · Aft', text, by, wo }].
 */
export function boatHistory(pieces, logs = {}) {
  const rows = [];
  pieces.forEach(p => Object.values(logs[p.id] || {}).forEach(e => {
    if (!e || !e.At) return;
    rows.push({ at: e.At, pieceId: p.id, piece: pieceName(p), text: describeLog(e), by: e.By || '', wo: e.WO || '' });
  }));
  return rows.sort((a, b) => b.at - a.at);
}
